import React, { useState, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';

import AppContext from '../../contexts/AppContext';
import {
  TwoColumnLayout,
  FormContent,
  StepContainer,
} from '../PrebookingSharedComponents';

import { CardContainer, SectionTitle, Field, FieldLabel, FieldValue, FieldGroup } from './SummaryShared';
import SummaryLanguageRow from './SummaryLanguageRow';
import SummaryDeliveryDate from './SummaryDeliveryDate';
import SummaryInstructions from './SummaryInstructions';
import SummaryMaterials from './SummaryMaterials';
import FilePreviewModal from './FilePreviewModal';
import SummaryCustomerInfo from './SummaryCustomerInfo';
import SummaryInvoicing from './SummaryInvoicing';

import i18n from '../../i18n';
import { getTranslationOrderLanguages } from '../../utils/languageUtils';

const TranslationOrderSummaryStep = ({ formData, getFile, uploadProgress }) => {
  const { user } = useContext(AppContext);
  const [previewAttachment, setPreviewAttachment] = useState(null);

  // Same selectable + target language union the language-selection step offers.
  const languages = getTranslationOrderLanguages(formData.selectableLanguages, formData.targetLanguages);
  const fromLanguage = languages.find(l => l.id === formData.fromLanguageId);
  const toLanguage = languages.find(l => l.id === formData.toLanguageId);

  const translatorTypes = (formData.translatorLevelIds || [])
    .map(id => (formData.translationLevels || []).find(level => level.id === id)?.name)
    .filter(Boolean)
    .join(', ');

  const handlePreview = useCallback((attachment) => {
    const file = getFile ? getFile(attachment.id) : attachment.file;
    if (!file) return;
    setPreviewAttachment({ ...attachment, file });
  }, [getFile]);

  const handleClosePreview = useCallback(() => {
    setPreviewAttachment(null);
  }, []);

  return (
    <StepContainer>
      {/* Details */}
      <CardContainer>
        <TwoColumnLayout>
          <SectionTitle>{i18n('translation_order_details')}</SectionTitle>
          <FormContent>
            <FieldGroup>
              <SummaryLanguageRow
                fromLanguage={fromLanguage}
                toLanguage={toLanguage}
              />
              {translatorTypes && (
                <Field>
                  <FieldLabel>{i18n('translation_order_translator_type')}</FieldLabel>
                  <FieldValue>{translatorTypes}</FieldValue>
                </Field>
              )}
              <SummaryDeliveryDate date={formData.desiredDeliveryDate} />
              <SummaryInstructions instructions={formData.instructionsForTranslator} />
              <SummaryMaterials
                attachments={formData.attachments}
                onPreview={handlePreview}
                progress={uploadProgress}
              />
              {user?.userGroup?.externalOrderIdUsed && (
                <Field>
                  <FieldLabel>{user?.userGroup?.externalOrderIdTitle || 'External order id'}</FieldLabel>
                  <FieldValue>{formData.externalOrderId || '-'}</FieldValue>
                </Field>
              )}
            </FieldGroup>
          </FormContent>
        </TwoColumnLayout>
      </CardContainer>

      <SummaryCustomerInfo formData={formData} />
      <SummaryInvoicing formData={formData} />

      {/* Preview Modal */}
      {previewAttachment && (
        <FilePreviewModal
          attachment={previewAttachment}
          onClose={handleClosePreview}
        />
      )}
    </StepContainer>
  );
};

TranslationOrderSummaryStep.propTypes = {
  formData: PropTypes.object.isRequired,
  getFile: PropTypes.func,
  uploadProgress: PropTypes.object,
};

export default TranslationOrderSummaryStep;
